import ExtendedPath from "csi/core/ExtendedPath";
import locale from "csi/locale";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import { C8AttributeId, C8NodeType } from "src/c8-platform/type/node";
import _ from "underscore";

/**
 * Reference: https://centric8.atlassian.net/wiki/spaces/DEV/pages/318439452/User+Defaults
 */

type UserDefaultValue = boolean | boolean[] | string | string[];

class UserDefault {
	/** indicates if blank values should also be shown when filtering by the user default */
	allowBlanks: boolean;

	/** indicates if the user default is optional (can be changed) or must be enforced */
	enforced: boolean;

	/** a list of other user defaults whose filters would take precedence over this one */
	filterSupersededBy: string[];

	/** unique combination of targetAttributeId and targetPath for given targetType */
	key: string;

	/** unique name for user default */
	name: string;

	/** the attribute where to apply the user default (as a default value or filter) */
	targetAttributeId: C8AttributeId;

	/** extended path from the target class to reach the target attribute */
	targetExtendedPath?: ExtendedPath;

	/** indicate if target attribute type is an array or not */
	targetAttributeIsArray: boolean;

	/** standard path from the target class to reach the target attribute */
	targetPath: string;

	/** the type of the class where the user default should apply  */
	targetType: C8NodeType;

	/** the attribute on class User from which to pull the default value (must be of type boolean or enum) */
	userAttributeId: C8AttributeId;

	/** current value of default */
	value: UserDefaultValue;

	/**
	 * Create a user default
	 */
	constructor(
		name: string,
		userAttributeId: C8AttributeId,
		targetType: C8NodeType,
		targetAttributeId: C8AttributeId,
		targetExtendedPath: ExtendedPath,
		isArray = false,
		enforced = false,
		allowBlanks = false,
		filterSupersededBy: string[] = [],
	) {
		this.name = name;
		this.userAttributeId = userAttributeId;
		this.targetType = targetType;
		this.targetAttributeId = targetAttributeId;
		this.targetAttributeIsArray = isArray;
		this.targetExtendedPath = targetExtendedPath;
		this.targetPath = targetExtendedPath?.getStandardPath() ?? "";
		this.enforced = enforced;
		this.allowBlanks = allowBlanks;
		this.filterSupersededBy = filterSupersededBy;
		this.key = xml.queryKey(targetAttributeId, null, this.targetPath);
	}

	/**
	 * Return key if part of the given matrix
	 */
	getMatrixKey(matrixExtendedPath: ExtendedPath): string {
		let path = this.targetPath;

		if (matrixExtendedPath) {
			path = xml.path(matrixExtendedPath.getStandardPath(), path);
		}

		return xml.queryKey(this.targetAttributeId, null, path);
	}

	/**
	 * Return array of values to use for ad-hoc query
	 */
	getQueryValues(): UserDefaultValue {
		console.assert(this.hasValidValue());
		const { allowBlanks, value } = this;
		const blankValue = allowBlanks ? [xml.VALUE_BLANK] : [];
		const defaultValue = (value == null) ? [] : value;
		return _.flatten([blankValue, defaultValue]);
	}

	/**
	 * Return type that owns target attribute
	 */
	getTargetDestinationType(): C8NodeType {
		return this.targetExtendedPath ? this.targetExtendedPath.getEndType() : this.targetType;
	}

	/**
	 * Return localized values of user default; default values are restricted to type enumlist, enum and boolean
	 */
	getTextValues(): string[] {
		const { value } = this;

		if (value == null) {
			return [];
		} else if (typeof value === "string") {
			return [locale.getEnumName(value)];
		} else if (Array.isArray(value)) {
			return value.map((enumValue) => locale.getEnumName(enumValue));
		}

		return [value.toString()];
	}

	/**
	 * Return value of default
	 */
	getValue(): UserDefaultValue {
		return this.value;
	}

	/**
	 * Return value of default based on target attribute type
	 */
	getValueForTargetAttributeType(): UserDefaultValue {
		const { targetAttributeIsArray, value } = this;

		if (targetAttributeIsArray) {
			return (typeof value === "string") ? [value] : value;
		}

		// if the default value is an array and target is not, then no default can be provided
		if (Array.isArray(value)) {
			return value.length == 1 ? value[0] : null;
		}

		return value;
	}

	/**
	 * Return true if value for default is not blank
	 */
	hasValidValue(): boolean {
		const { value } = this;

		if ((value == null) ||
			(typeof value === "string" && !xml.enumGetText(value)) ||
			(Array.isArray(value) && !value.length)
		) {
			return false;
		}

		return true;
	}

	/**
	 * Apply user default filter condition to given query
	 * @param matrixExtendedPath - if working with matrix, then path to matrix is given
	 */
	toQuery(sourceQuery: Query, matrixExtendedPath?: ExtendedPath): void {
		const { allowBlanks, targetAttributeId, targetExtendedPath, value } = this;
		const ep = matrixExtendedPath ? matrixExtendedPath.clone().append(targetExtendedPath) : targetExtendedPath;
		const path = ep ? ep.getQueryPath() : null;
		const udQuery = new Query();
		let q = udQuery;
		let blankOp: string;

		if (allowBlanks) {
			if (value === true || value === false) {
				blankOp = xml.OP_BLANKS + xml.OP_BLANK_DEFAULT;
			} else {
				blankOp = xml.OP_BLANKS + xml.OP_BLANK_ENUM;
			}
		}

		if (!Array.isArray(value)) {
			if (blankOp) {
				q = q.Or();
				q.Attr(targetAttributeId, blankOp, null, path);
			}

			q.Attr(targetAttributeId, xml.OP_EQUALS, value, path);
		} else if (value.length) {
			q = q.Or();

			if (blankOp) {
				q.Attr(targetAttributeId, blankOp, null, path);
			}

			value.forEach((v) => {
				q.Attr(targetAttributeId, xml.OP_EQUALS, v, path);
			});
		}

		// insert filters near beginning of query to (supposedly) improve performance
		sourceQuery.importQuery(udQuery, sourceQuery.getElementAfterTypeFilter());
	}
}

export = UserDefault;